'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FormField } from '@/components/molecules/FormField';
import { Button } from '@/components/atoms/Button';
import styles from './ContactForm.module.scss';

export interface ContactFormProps {
  formType: 'technical' | 'billing' | 'quote' | 'demo' | 'upgrade' | 'general';
  webhookUrl?: string;
  className?: string;
}

export interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  subject: string;
  message: string;
  urgency?: 'low' | 'medium' | 'high';
  budget?: string;
  timeline?: string;
}

export function ContactForm({
  formType,
  webhookUrl: _webhookUrl,
  className,
}: ContactFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    urgency: 'medium',
    budget: '',
    timeline: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'お名前は必須項目です';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスは必須項目です';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    if (!formData.company.trim()) {
      newErrors.company = '会社名は必須項目です';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = '件名は必須項目です';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'お問い合わせ内容は必須項目です';
    } else if (formData.message.length < 10) {
      newErrors.message = 'お問い合わせ内容は10文字以上で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Here you would normally send the data to your webhook
      // For now, we'll simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSubmitted(true);
    } catch (_error) {
      setSubmitError('送信に失敗しました。しばらくしてからもう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const getFormTitle = () => {
    switch (formType) {
      case 'technical':
        return '技術サポートフォーム';
      case 'billing':
        return '請求・料金フォーム';
      case 'quote':
        return '見積もり依頼フォーム';
      case 'demo':
        return 'デモンストレーションフォーム';
      case 'upgrade':
        return 'アップグレードフォーム';
      default:
        return 'お問い合わせフォーム';
    }
  };

  const formClasses = [
    styles.contactForm,
    styles[formType],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (isSubmitted) {
    return (
      <motion.div
        className={styles.successMessage}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className={styles.successIcon}>
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="32" fill="#10B981" />
            <path d="M20 32l8 8 16-16" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3>お問い合わせありがとうございました</h3>
        <p>
          内容を確認の上、2営業日以内にご回答いたします。<br />
          お急ぎの場合は、お電話（03-5948-4959）でお問い合わせください。
        </p>
      </motion.div>
    );
  }

  return (
    <form className={formClasses} onSubmit={handleSubmit}>
      <div className={styles.formHeader}>
        <h2>{getFormTitle()}</h2>
        <p>以下のフォームにご記入いただき、送信してください。</p>
      </div>

      <div className={styles.formGrid}>
        <div className={styles.formRow}>
          <FormField
            type="input"
            label="お名前"
            isRequired
            error={errors.name}
            inputProps={{
              value: formData.name,
              onChange: (e) => handleInputChange('name', e.target.value),
              placeholder: '山田 太郎',
            }}
          />
        </div>

        <div className={styles.formRow}>
          <FormField
            type="input"
            label="メールアドレス"
            isRequired
            error={errors.email}
            inputProps={{
              type: 'email',
              value: formData.email,
              onChange: (e) => handleInputChange('email', e.target.value),
              placeholder: 'yamada@example.com',
            }}
          />
        </div>

        <div className={styles.formRow}>
          <FormField
            type="input"
            label="会社名"
            isRequired
            error={errors.company}
            inputProps={{
              value: formData.company,
              onChange: (e) => handleInputChange('company', e.target.value),
              placeholder: '株式会社サンプル',
            }}
          />
        </div>

        <div className={styles.formRow}>
          <FormField
            type="input"
            label="電話番号"
            error={errors.phone}
            helperText="任意項目です"
            inputProps={{
              type: 'tel',
              value: formData.phone,
              onChange: (e) => handleInputChange('phone', e.target.value),
              placeholder: '03-1234-5678',
            }}
          />
        </div>

        <div className={styles.formRow}>
          <FormField
            type="input"
            label="件名"
            isRequired
            error={errors.subject}
            inputProps={{
              value: formData.subject,
              onChange: (e) => handleInputChange('subject', e.target.value),
              placeholder: 'お問い合わせ件名',
            }}
          />
        </div>

        {/* Form type specific fields */}
        {(formType === 'quote' || formType === 'demo' || formType === 'upgrade') && (
          <>
            <div className={styles.formRow}>
              <FormField
                type="input"
                label="予算"
                helperText="おおよその予算をお聞かせください"
                inputProps={{
                  value: formData.budget,
                  onChange: (e) => handleInputChange('budget', e.target.value),
                  placeholder: '100万円～500万円',
                }}
              />
            </div>

            <div className={styles.formRow}>
              <FormField
                type="input"
                label="希望時期"
                helperText="導入希望時期をお聞かせください"
                inputProps={{
                  value: formData.timeline,
                  onChange: (e) => handleInputChange('timeline', e.target.value),
                  placeholder: '2025年3月頃',
                }}
              />
            </div>
          </>
        )}

        <div className={styles.formRow}>
          <FormField
            type="textarea"
            label="お問い合わせ内容"
            isRequired
            error={errors.message}
            helperText="詳細な内容をお聞かせください（10文字以上）"
            textareaProps={{
              value: formData.message,
              onChange: (e) => handleInputChange('message', e.target.value),
              placeholder: '具体的なお問い合わせ内容をご記入ください...',
              minRows: 5,
              maxRows: 10,
            }}
          />
        </div>
      </div>

      <AnimatePresence>
        {submitError && (
          <motion.div
            className={styles.errorMessage}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {submitError}
          </motion.div>
        )}
      </AnimatePresence>

      <div className={styles.formActions}>
        <Button
          type="submit"
          variant="primary"
          size="large"
          isLoading={isSubmitting}
          loadingText="送信中..."
          disabled={isSubmitting}
          className={styles.submitButton}
        >
          お問い合わせを送信
        </Button>
      </div>

      <div className={styles.formFooter}>
        <p>
          送信いただいた情報は、プライバシーポリシーに基づいて適切に管理いたします。
        </p>
      </div>
    </form>
  );
}

export default ContactForm;